// src/pages/Schedule/EmployeeWeeklyView.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../lib/firebase";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import useTournamentWeek from "../../hooks/useTournamentWeek";
import useScheduleEmployees from "../../hooks/useScheduleEmployees";
import useWeekScheduleData from "../../hooks/useWeekScheduleData";

import {
  addDays,
  fmtDay,
  isISODate,
  startOfWeekMonday,
  ymd,
} from "../../utils/scheduleDateUtils";

import {
  getEmployeeName,
  getEmployeePosition,
  isEmployeeAvailableForDate,
} from "../../utils/scheduleEmployeeUtils";

import {
  normalizeDay,
  serializeDay,
} from "../../utils/scheduleDayUtils";

import {
  getEmployeeAssignmentsForDay,
} from "../../utils/scheduleValidationUtils";

import {
  hoursBetweenTimes,
} from "../../utils/scheduleTimeUtils";

import "./EmployeeWeeklyView.css";

function shortDayLabel(date) {
  return fmtDay(date);
}

function prettyAssignmentLabel(a) {
  const role = a?.roleLabel || a?.roleKey || "";
  const start = a?.start || "";
  const end = a?.end || "";

  if (!role && !start && !end) return "—";
  if (start && end) return `${role} (${start}-${end})`;
  return role || "—";
}

export default function EmployeeWeeklyView() {
  const navigate = useNavigate();

  const { twLoading, initialWeekStart } = useTournamentWeek({
    db,
    doc,
    getDoc,
    isISODate,
    startOfWeekMonday,
  });

  const [weekStart, setWeekStart] = useState(initialWeekStart);
  const [search, setSearch] = useState("");
  const [positionFilter, setPositionFilter] = useState("All");
  const [showUnscheduledOnly, setShowUnscheduledOnly] = useState(false);
  const [hideUnscheduled, setHideUnscheduled] = useState(false);
  const [availabilityOnly, setAvailabilityOnly] = useState(false);

  useEffect(() => {
    setWeekStart(initialWeekStart);
  }, [initialWeekStart]);

  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const weekLabel = useMemo(() => {
    if (!weekDates.length) return "";
    return `${fmtDay(weekDates[0])} - ${fmtDay(weekDates[6])}`;
  }, [weekDates]);

  const { employees } = useScheduleEmployees({
    db,
    collection,
    getDocs,
    getEmployeeName,
  });

  const { weekDaysData, loading, errMsg } = useWeekScheduleData({
    weekDates,
    ymd,
    normalizeDay,
    serializeDay,
  });

  const positionOptions = useMemo(() => {
    const vals = Array.from(
      new Set(
        (employees || [])
          .map((e) => String(getEmployeePosition(e) || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));

    return ["All", ...vals];
  }, [employees]);

  const employeeRows = useMemo(() => {
    return (employees || []).map((emp) => {
      const byDay = {};
      let totalDays = 0;
      let totalHours = 0;

      for (const d of weekDates) {
        const iso = ymd(d);
        const day = normalizeDay(weekDaysData?.[iso] || {});
        const assignments = getEmployeeAssignmentsForDay(day, emp.id) || [];
        const available = isEmployeeAvailableForDate(emp, iso);

        const normalizedAssignments = assignments.map((a) => ({
          ...a,
          roleLabel: a?.roleLabel || a?.roleKey || "",
        }));

        const hours = normalizedAssignments.reduce(
          (sum, a) => sum + hoursBetweenTimes(a.start, a.end),
          0
        );

        if (normalizedAssignments.length > 0) totalDays += 1;
        totalHours += hours;

        byDay[iso] = {
          iso,
          date: d,
          available,
          assignments: normalizedAssignments,
          hours,
          scheduled: normalizedAssignments.length > 0,
        };
      }

      return {
        emp,
        name: getEmployeeName(emp),
        position: getEmployeePosition(emp) || "",
        totalDays,
        totalHours,
        byDay,
      };
    });
  }, [employees, weekDates, weekDaysData]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return employeeRows
      .filter((row) => {
        if (positionFilter !== "All" && row.position !== positionFilter) return false;

        if (q) {
          const haystack = [row.name, row.position, row.emp?.email || ""]
            .join(" ")
            .toLowerCase();

          if (!haystack.includes(q)) return false;
        }

        if (showUnscheduledOnly && row.totalDays > 0) return false;
        if (hideUnscheduled && row.totalDays === 0) return false;

        if (availabilityOnly) {
          const hasAnyAvailableDay = weekDates.some((d) => {
            const iso = ymd(d);
            return row.byDay?.[iso]?.available;
          });
          if (!hasAnyAvailableDay) return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (a.totalDays !== b.totalDays) return b.totalDays - a.totalDays;
        return String(a.name).localeCompare(String(b.name));
      });
  }, [
    employeeRows,
    search,
    positionFilter,
    showUnscheduledOnly,
    hideUnscheduled,
    availabilityOnly,
    weekDates,
  ]);

  const scheduledCount = filteredRows.filter((r) => r.totalDays > 0).length;
  const unscheduledCount = filteredRows.filter((r) => r.totalDays === 0).length;

  return (
    <div className="employee-weekly-view-page">
      <div className="employee-weekly-topbar">
        <div className="employee-weekly-topline">
          <div className="employee-weekly-title-wrap">
            <h1 className="employee-weekly-title">Employee Weekly View</h1>
            <div className="employee-weekly-weektext">{weekLabel}</div>
          </div>

          <div className="employee-weekly-actions">
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => navigate("/admin")}
            >
              Return to Admin Portal
            </button>

            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => navigate("/admin/schedule")}
            >
              Back to Schedule
            </button>
          </div>
        </div>

        <div className="employee-weekly-controls">
          <input
            className="employee-weekly-search"
            type="text"
            placeholder="Search employee / email / position..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="employee-weekly-select"
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value)}
          >
            {positionOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>

          <label className="employee-weekly-check">
            <input
              type="checkbox"
              checked={showUnscheduledOnly}
              onChange={(e) => {
                setShowUnscheduledOnly(e.target.checked);
                if (e.target.checked) setHideUnscheduled(false);
              }}
            />
            Unscheduled Only
          </label>

          <label className="employee-weekly-check">
            <input
              type="checkbox"
              checked={hideUnscheduled}
              onChange={(e) => {
                setHideUnscheduled(e.target.checked);
                if (e.target.checked) setShowUnscheduledOnly(false);
              }}
            />
            Hide Unscheduled
          </label>

          <label className="employee-weekly-check">
            <input
              type="checkbox"
              checked={availabilityOnly}
              onChange={(e) => setAvailabilityOnly(e.target.checked)}
            />
            Available Only
          </label>
        </div>

        <div className="employee-weekly-summarybar">
          <div className="employee-weekly-summarycard">
            <div className="employee-weekly-summarylabel">Employees</div>
            <div className="employee-weekly-summaryvalue">{filteredRows.length}</div>
          </div>

          <div className="employee-weekly-summarycard">
            <div className="employee-weekly-summarylabel">Scheduled</div>
            <div className="employee-weekly-summaryvalue">{scheduledCount}</div>
          </div>

          <div className="employee-weekly-summarycard">
            <div className="employee-weekly-summarylabel">Unscheduled</div>
            <div className="employee-weekly-summaryvalue">{unscheduledCount}</div>
          </div>
        </div>
      </div>

      {(loading || twLoading) && <div style={{ padding: 12 }}>Loading…</div>}

      {!!errMsg && (
        <div
          style={{
            padding: 12,
            color: "#991b1b",
            background: "#fff1f2",
            borderRadius: 12,
            marginBottom: 10,
          }}
        >
          {errMsg}
        </div>
      )}

      {!loading && !twLoading && !errMsg && (
        <div className="employee-weekly-table-wrap">
          <table className="employee-weekly-table">
            <thead>
              <tr>
                <th className="sticky-col sticky-col-name">Employee</th>
                <th>Position</th>
                {weekDates.map((d) => (
                  <th key={ymd(d)}>{shortDayLabel(d)}</th>
                ))}
                <th>Days</th>
                <th>Hours</th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.emp.id}
                  className={row.totalDays > 0 ? "row-scheduled" : "row-unscheduled"}
                >
                  <td className="sticky-col sticky-col-name employee-name-cell">
                    <div className="employee-name-main">{row.name}</div>
                    {row.emp?.email ? (
                      <div className="employee-name-sub">{row.emp.email}</div>
                    ) : null}
                  </td>

                  <td className="employee-position-cell">{row.position || "—"}</td>

                  {weekDates.map((d) => {
                    const iso = ymd(d);
                    const cell = row.byDay?.[iso];

                    return (
                      <td
                        key={iso}
                        className={[
                          "employee-day-cell",
                          cell?.scheduled ? "scheduled-cell" : "",
                          cell?.available && !cell?.scheduled ? "available-cell" : "",
                          !cell?.available ? "unavailable-cell" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        title={
                          cell?.scheduled
                            ? cell.assignments.map((a) => prettyAssignmentLabel(a)).join(" | ")
                            : cell?.available
                            ? "Available"
                            : "Not available"
                        }
                      >
                        {cell?.scheduled ? (
                          <div className="employee-day-assignments">
                            {cell.assignments.map((a) => (
                              <div key={a.assignmentId} className="employee-day-pill">
                                {prettyAssignmentLabel(a)}
                              </div>
                            ))}
                          </div>
                        ) : cell?.available ? (
                          <span className="employee-day-muted">Available</span>
                        ) : (
                          <span className="employee-day-muted">—</span>
                        )}
                      </td>
                    );
                  })}

                  <td className="employee-total-cell">{row.totalDays}</td>
                  <td className="employee-total-cell">{row.totalHours.toFixed(1)}</td>
                </tr>
              ))}

              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={weekDates.length + 4} className="employee-weekly-empty">
                    No employees matched your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}